/**
 * Cross-Sell utilities.
 *
 * Show related listings on listing pages to increase discovery.
 *
 * Related listings can be:
 *   - Same category
 *   - Same provider (provider's other listings)
 *   - Manually curated (listing.publicData.relatedListingIds = ['id1', 'id2'])
 *   - Price range similar (± 30%)
 *
 * Sorting: curated → same provider → same category → price range
 */

export const CROSS_SELL_LIMIT = 6;
export const PRICE_RANGE_TOLERANCE = 0.30; // ±30%

/**
 * Get manually curated related listing IDs.
 * @param {Object|null} listing
 * @returns {string[]}
 */
export const getCuratedRelated = (listing) =>
  listing?.attributes?.publicData?.relatedListingIds || [];

/**
 * Filter listings by same category.
 * @param {Object[]} listings
 * @param {string} category
 * @param {string} excludeId — the current listing ID
 * @returns {Object[]}
 */
export const filterByCategory = (listings, category, excludeId) => {
  if (!Array.isArray(listings) || !category) return [];
  return listings.filter(l =>
    l.id?.uuid !== excludeId &&
    l.attributes?.publicData?.category === category
  );
};

/**
 * Filter listings within a price range.
 * @param {Object[]} listings
 * @param {number} priceCents — target price
 * @param {string} excludeId
 * @returns {Object[]}
 */
export const filterByPriceRange = (listings, priceCents, excludeId) => {
  if (!Array.isArray(listings) || !priceCents) return [];
  const min = priceCents * (1 - PRICE_RANGE_TOLERANCE);
  const max = priceCents * (1 + PRICE_RANGE_TOLERANCE);
  return listings.filter(l => {
    if (l.id?.uuid === excludeId) return false;
    const price = l.attributes?.price?.amount;
    return price >= min && price <= max;
  });
};

/**
 * Score and sort related listings (higher score = more relevant).
 * @param {Object[]} candidates
 * @param {Object} current — current listing
 * @param {string[]} curatedIds
 * @returns {Object[]}
 */
export const rankRelated = (candidates, current, curatedIds = []) => {
  const category = current?.attributes?.publicData?.category;
  const providerId = current?.relationships?.author?.data?.id?.uuid;
  const price = current?.attributes?.price?.amount;

  return [...candidates]
    .map(l => {
      let score = 0;
      if (curatedIds.includes(l.id?.uuid)) score += 100;
      if (providerId && l.relationships?.author?.data?.id?.uuid === providerId) score += 20;
      if (category && l.attributes?.publicData?.category === category) score += 10;
      if (price) {
        const lPrice = l.attributes?.price?.amount;
        if (lPrice && Math.abs(lPrice - price) / price <= PRICE_RANGE_TOLERANCE) score += 5;
      }
      return { listing: l, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, CROSS_SELL_LIMIT)
    .map(({ listing }) => listing);
};
